import { useRef } from 'react';

import { useAppServices } from '@/app/AppServicesContext';
import { MEDIA } from '@/assets/media';
import { useContent } from '@/content/ContentContext';
import { useCosmos } from '@/features/cosmos/CosmosContext';
import { useJourneyChoreography } from '@/features/hero/useJourneyChoreography';
import { Button, ButtonLink } from '@/ui/Button/Button';
import { cx } from '@/ui/classNames';
import { Container } from '@/ui/Layout/Container';
import { RevealText } from '@/ui/Reveal/RevealText';
import { typography } from '@/ui/Typography/typography';

import styles from './HeroJourney.module.css';

/**
 * Ouverture du site : un trou noir calculé en temps réel par le décor cosmique.
 * Le défilement de cette longue section rapproche la caméra puis la fait
 * plonger à travers l'horizon. Sans GPU suffisant ou avec le mouvement réduit,
 * l'affiche fixe prend le relais.
 */
export function HeroJourney() {
  const { hero } = useContent();
  const { device, motion } = useAppServices();
  const { ready } = useCosmos();
  const immersive = device.canRenderCosmos;
  const poster = device.portrait ? MEDIA.blackHolePortrait : MEDIA.blackHoleLandscape;

  const sectionRef = useRef<HTMLElement>(null);
  useJourneyChoreography(sectionRef, immersive);

  const crossHorizon = (): void => {
    const section = sectionRef.current;
    if (!section) return;
    motion.scrollTo(section.offsetTop + section.offsetHeight - window.innerHeight, { duration: 8 });
  };

  return (
    <section
      ref={sectionRef}
      id="top"
      aria-labelledby="hero-title"
      className={cx(styles.hero, immersive && styles.immersive, ready && styles.ready)}
      data-cosmos-chapter="origin"
    >
      <div className={styles.stage}>
        <img
          className={styles.poster}
          src={poster.src}
          width={poster.width}
          height={poster.height}
          alt={hero.posterAlt}
          fetchPriority="high"
          decoding="async"
        />
        <div className={styles.scrim} aria-hidden="true" />

        <Container className={styles.intro} data-journey="intro">
          <div className={styles.introInner}>
            <RevealText
              as="h1"
              id="hero-title"
              trigger="mount"
              delay={0.2}
              className={cx(typography.heading, typography.display, styles.title)}
            >
              {hero.title} <span className={typography.accent}>{hero.titleAccent}</span>
            </RevealText>
            <p className={cx(typography.lead, styles.lead)}>{hero.lead}</p>
            <div className={styles.actions}>
              <ButtonLink href={hero.cta.href}>{hero.cta.label}</ButtonLink>
              {immersive && (
                <Button variant="ghost" icon="arrow-down" onClick={crossHorizon}>
                  {hero.journeyCta}
                </Button>
              )}
            </div>
          </div>
        </Container>

        {immersive && (
          <ol className={styles.beats}>
            {hero.beats.map((beat) => (
              <li key={beat.word} className={styles.beat} data-journey="beat">
                <span className={styles.beatWord}>{beat.word}</span>
                <span className={styles.beatLine}>{beat.line}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}
